import { useEffect, useRef, useState } from "react";
import { Coords } from "@/data/coords.data";

const DROPDOWN_OFFSET = 5;

type DropdownMenuProps = {
  dropdownOffset?: number;
  syncWidthWithHeader?: boolean;
};

export const useDropdownMenuWithOutsideClosing = ({
  dropdownOffset = DROPDOWN_OFFSET,
  syncWidthWithHeader,
}: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLImageElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      !dropdownRef.current?.contains(event.target as Node) &&
      !buttonRef.current?.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  const getCoords = (): Coords | null => {
    const box = buttonRef?.current?.getBoundingClientRect();
    return box
      ? {
          left: box.left,
          right: box.right,
          top: box.top,
          bottom: box.bottom,
          width: box.width,
          height: box.height,
        }
      : null;
  };

  const adjustPosition = (coords: Coords) => {
    if (!dropdownRef.current || !buttonRef.current) {
      return;
    }

    const content = dropdownRef.current;

    // TODO: handle content outside top/bottom borders of the screen
    content.style.top = `${coords.bottom + dropdownOffset}px`;

    if (syncWidthWithHeader) {
      content.style.width = `${buttonRef.current.offsetWidth}px`;
      content.style.left = `${coords.left}px`;
      content.style.right = `${coords.right}px`;

      return;
    }

    content.style.left = "unset";
    content.style.right = "unset";

    const rect = content.getBoundingClientRect();
    const contentWidth = rect.width;

    const left = (coords.left + coords.right - contentWidth) / 2;
    const right = left + contentWidth;

    const viewportWidth = window.innerWidth;

    if (left < 0) {
      //if outside the left border of the screen
      content.style.left = `${dropdownOffset}px`;
    } else if (viewportWidth < right) {
      //if outside the right border of the screen
      content.style.right = `${dropdownOffset}px`;
    } else {
      content.style.left = `${left}px`;
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      const coords = getCoords();

      if (!coords) {
        return;
      }

      adjustPosition(coords);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen, dropdownRef.current]);

  return {
    dropdownRef,
    buttonRef,
    isOpen,
    setIsOpen,
  };
};
